import { z } from 'zod';

// ── Create API Key ─────────────────────────────────────────────────
export const createApiKeySchema = z.object({
    app_name: z.string().min(1, 'App name is required').max(50, 'Max 50 characters'),
});

// ── Update API Key ─────────────────────────────────────────────────
export const updateApiKeySchema = z.object({
    is_active: z.boolean(),
});

// ── Wallet Limits ──────────────────────────────────────────────────
export const walletLimitsSchema = z.object({
    currency_code: z.string().length(3, 'Currency code must be 3 characters'),
    wallet_type: z.enum(['real_money', 'reward'], { errorMap: () => ({ message: 'Select a wallet type' }) }),
    max_balance: z.coerce.number().positive('Must be positive').optional(),
    min_transaction: z.coerce.number().positive('Must be positive').optional(),
    max_transaction: z.coerce.number().positive('Must be positive').optional(),
    daily_transaction_limit: z.coerce.number().positive('Must be positive').optional(),
    is_active: z.boolean().optional(),
});

// ── Credit Reward ──────────────────────────────────────────────────
export const creditRewardSchema = z.object({
    user_id: z.array(z.number().int().positive()).min(1, 'Select at least one user'),
    amount: z.coerce.number().positive('Amount must be positive'),
    currency_code: z.string().length(3, 'Currency code must be 3 characters'),
    description: z.string().optional().or(z.literal('')),
    reference_id: z.string().optional().or(z.literal('')),
    reference_type: z.string().optional().or(z.literal('')),
    expires_at: z.string().optional().or(z.literal('')),
});

// ── Closeout ───────────────────────────────────────────────────────
export const closeoutSchema = z.object({
    wallet_id: z.coerce.number().int().positive('Wallet ID is required'),
    payout_method: z.enum(
        ['bank_transfer', 'check', 'cash', 'financial_aid_reversal', 'other'],
        { errorMap: () => ({ message: 'Please select a payout method' }) }
    ),
    reason: z.enum(
        ['closeout', 'withdrawal', 'graduation', 'termination', 'other'],
        { errorMap: () => ({ message: 'Please select a reason' }) }
    ),
    notes: z.string().optional().or(z.literal('')),
});

// ── Settlement Generate ────────────────────────────────────────────
export const settlementSchema = z
    .object({
        app_source: z.string().min(1, 'App source is required'),
        currency_code: z.string().length(3, 'Currency code must be 3 characters'),
        period_start: z.string().min(1, 'Start date is required'),
        period_end: z.string().min(1, 'End date is required'),
    })
    .refine((d) => new Date(d.period_end) > new Date(d.period_start), {
        message: 'End date must be after start date',
        path: ['period_end'],
    });

// ── Revenue Report ─────────────────────────────────────────────────
export const revenueReportSchema = z.object({
    app_source: z.string().optional().or(z.literal('')),
    currency_code: z.string().length(3, 'Currency code must be 3 characters'),
    period_start: z.string().min(1, 'Start date is required'),
    period_end: z.string().min(1, 'End date is required'),
    group_by: z.enum(['day', 'week', 'month']).optional(),
});

// ── Payout Action ──────────────────────────────────────────────────
export const payoutProcessSchema = z.object({
    payout_reference: z.string().min(1, 'Payout reference is required'),
});

// ── Admin Hold ─────────────────────────────────────────────────────
export const adminHoldSchema = z.object({
    wallet_id: z.coerce.number().int().positive('Wallet ID is required'),
    amount: z.coerce.number().positive('Amount must be positive'),
    reference_id: z.string().optional().or(z.literal('')),
    reference_type: z.string().optional().or(z.literal('')),
    expires_at: z.string().optional().or(z.literal('')),
});
